// eslint configured: rule not present in CRA build
import { useState } from "react";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { apiMutate, client } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";

const CREATOR_TYPES = [
  { id: "youtuber", label: "YouTuber", note: "Long-form, deep takes." },
  { id: "shorts", label: "Shorts / Reels", note: "Fast hooks, short loops." },
  { id: "writer", label: "Writer", note: "Blog, newsletter, essays." },
  { id: "twitter", label: "X / Threads", note: "Threads, microposts." },
  { id: "multi", label: "Multi-platform", note: "All of the above." },
];

const FREQUENCIES = [
  { id: "daily", label: "Daily", note: "Most days of the week." },
  { id: "weekly", label: "Weekly", note: "A few drops a week." },
  { id: "monthly", label: "Monthly", note: "Quality over quantity." },
  { id: "custom", label: "Custom", note: "Your own rhythm." },
];

const PIPELINE_TEMPLATES = {
  youtuber: { platform: "youtube_long", title: "First long-form video — pick a topic" },
  shorts: { platform: "reels", title: "First Reel — hook + payoff in 30s" },
  writer: { platform: "blog", title: "First essay — pick a thesis" },
  twitter: { platform: "tweet", title: "First thread — 7 tweets, one idea" },
  multi: { platform: "youtube_long", title: "First idea — pick your platform" },
};

export default function OnboardingModal({ user, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const [creatorType, setCreatorType] = useState(user?.preferences?.creator_type || "");
  const [frequency, setFrequency] = useState(user?.preferences?.publishing_frequency || "");
  const [firstIdea, setFirstIdea] = useState("");
  const [saving, setSaving] = useState(false);

  const finish = async (skipIdea = false) => {
    setSaving(true);
    try {
      // Save preferences
      await client.patch("/auth/preferences", {
        creator_type: creatorType,
        publishing_frequency: frequency,
        onboarded: true,
      });
      // Always load demo workspace for new users (launch-prep #2)
      try {
        await client.post("/demo/seed", {});
      } catch { /* non-blocking */ }
      try {
        track(EVENTS.ONBOARDING_COMPLETED, { creator_type: creatorType, frequency });
        track(EVENTS.DEMO_LOADED, { trigger: "onboarding" });
      } catch { /* noop */ }
      // Create first idea (optional)
      const idea = (firstIdea || "").trim();
      if (!skipIdea && idea) {
        const tpl = PIPELINE_TEMPLATES[creatorType] || PIPELINE_TEMPLATES.multi;
        await apiMutate("post", "/ideas", {
          title: idea,
          platform: tpl.platform,
          tags: ["first"],
          stage: "idea",
        });
        try { track(EVENTS.IDEA_CREATED, { source: "onboarding_first_idea" }); } catch { /* noop */ }
      }
      onComplete?.();
    } finally {
      setSaving(false);
    }
  };

  const next = () => setStep((s) => Math.min(2, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const canNext = step === 0 ? !!creatorType : step === 1 ? !!frequency : true;
  const tpl = PIPELINE_TEMPLATES[creatorType] || PIPELINE_TEMPLATES.multi;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end md:items-center justify-center backdrop-blur-sm"
      style={{ background: "rgba(0,0,0,0.78)" }}
      data-testid="onboarding-modal"
    >
      <div
        className="w-full md:max-w-xl rounded-t-3xl md:rounded-3xl max-h-[92vh] overflow-y-auto"
        style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
      >
        {/* Progress */}
        <div className="px-6 md:px-10 pt-8 md:pt-10">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">
              <span className="seal-dot mr-2 align-middle" /> Setup · {step + 1} of 3
            </div>
            <button onClick={onClose} className="btn-ghost text-xs" data-testid="onboarding-skip">
              Skip setup
            </button>
          </div>
          <div className="flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-1 rounded-full flex-1 transition"
                style={{ background: i <= step ? "var(--seal)" : "var(--ink-22)" }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 pt-8 pb-4">
          {step === 0 && (
            <div>
              <h2
                className="font-heading text-3xl md:text-4xl leading-[1.05]"
                style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}
              >
                What kind of <span className="font-display-italic">creator</span> are you?
              </h2>
              <p className="text-sm mt-3" style={{ color: "var(--ink-72)" }}>
                We&apos;ll tune PintaFlow to your craft. Change anytime.
              </p>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {CREATOR_TYPES.map((c) => {
                  const active = creatorType === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setCreatorType(c.id)}
                      data-testid={`onboarding-type-${c.id}`}
                      className="text-left rounded-2xl p-4 transition"
                      style={{
                        background: active ? "rgba(216,168,87,0.10)" : "transparent",
                        border: active ? "1px solid var(--seal)" : "1px solid var(--ink-22)",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div
                          className="font-heading text-lg"
                          style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}
                        >
                          {c.label}
                        </div>
                        {active && (
                          <span
                            className="w-5 h-5 rounded-full flex items-center justify-center"
                            style={{ background: "var(--seal)", color: "var(--bg)" }}
                          >
                            <Check size={11} strokeWidth={3} />
                          </span>
                        )}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "var(--ink-55)" }}>
                        {c.note}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2
                className="font-heading text-3xl md:text-4xl leading-[1.05]"
                style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}
              >
                How often do you <span className="font-display-italic">publish</span>?
              </h2>
              <p className="text-sm mt-3" style={{ color: "var(--ink-72)" }}>
                We&apos;ll suggest goals and streak targets to match.
              </p>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {FREQUENCIES.map((f) => {
                  const active = frequency === f.id;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFrequency(f.id)}
                      data-testid={`onboarding-freq-${f.id}`}
                      className="text-left rounded-2xl p-4 transition"
                      style={{
                        background: active ? "rgba(216,168,87,0.10)" : "transparent",
                        border: active ? "1px solid var(--seal)" : "1px solid var(--ink-22)",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div
                          className="font-heading text-lg"
                          style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}
                        >
                          {f.label}
                        </div>
                        {active && (
                          <span
                            className="w-5 h-5 rounded-full flex items-center justify-center"
                            style={{ background: "var(--seal)", color: "var(--bg)" }}
                          >
                            <Check size={11} strokeWidth={3} />
                          </span>
                        )}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "var(--ink-55)" }}>
                        {f.note}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2
                className="font-heading text-3xl md:text-4xl leading-[1.05]"
                style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}
              >
                Capture your first <span className="font-display-italic">spark</span>.
              </h2>
              <p className="text-sm mt-3" style={{ color: "var(--ink-72)" }}>
                One sentence is enough. It&apos;ll land in your <span style={{ color: "var(--ink)" }}>Vault</span> and the
                first room of the pipeline.
              </p>
              <input
                value={firstIdea}
                onChange={(e) => setFirstIdea(e.target.value)}
                className="input mt-7 text-base"
                placeholder={tpl.title}
                data-testid="onboarding-first-idea"
                autoFocus
                style={{ fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 450" }}
              />
              <p className="text-xs mt-3" style={{ color: "var(--ink-55)" }}>
                Or leave blank — PintaFlow won&apos;t mind. A sample workspace will load so you can feel the studio immediately.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 md:px-10 py-5 flex items-center justify-between gap-2"
          style={{ borderTop: "1px solid var(--ink-22)" }}
        >
          <button
            onClick={back}
            disabled={step === 0}
            className="btn-ghost disabled:opacity-30"
            data-testid="onboarding-back"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {step < 2 ? (
            <button
              onClick={next}
              disabled={!canNext}
              className="btn-primary disabled:opacity-40"
              data-testid="onboarding-next"
            >
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={() => finish(false)}
              disabled={saving}
              className="btn-primary disabled:opacity-50"
              data-testid="onboarding-finish"
            >
              {saving ? "Saving…" : "Open the studio"} <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
